import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Brain, FileText, ShieldCheck, Zap, Crosshair, Map } from 'lucide-react';
import { FAQAccordion } from './components/FAQAccordion';
import { TestimonialCarousel } from './components/TestimonialCarousel';
import { Countdown } from './components/Countdown';
import { PurchasePopup } from './components/PurchasePopup';
const COVER_MURDOKU = 'https://cdn.magicpatterns.com/uploads/avzypTqfjyDGkvE2n5EqaY/PORTADA_MURDOKU.webp';
const COVER_VIAJE = 'https://cdn.magicpatterns.com/uploads/d3mRg8WCDVPjbfZzPNAShc/MURDOKU_VIAJE_AL_PASADO.webp';
const PACK_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/e21U2i9cTMQ8d8beuW1Z2B/MURDOKUS.png';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/4wzELUP6qiRd981MUJi4s6/IMG_8693.MOV';
const CHECKOUT_URL = 'https://murdoku.impultienda.ar/checkout/pack-murdoku';
export function App() {
  const [showFloatingBtn, setShowFloatingBtn] = useState(false);
  const [currency, setCurrency] = useState<'USD' | 'ARS'>('USD');
  useEffect(() => {
    fetch('https://api.country.is/').then(res => res.json()).then(data => {
      if (data.country === 'AR') {
        setCurrency('ARS');
      }
    }).catch(err => console.error('Geolocation error:', err));
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setShowFloatingBtn(true);
      } else {
        setShowFloatingBtn(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // Meta Pixel Code
    const win = window as any;
    if (!win.fbq) {
      win.fbq = function () {
        win.fbq.callMethod ? win.fbq.callMethod.apply(win.fbq, arguments) : win.fbq.queue.push(arguments);
      };
      if (!win._fbq) win._fbq = win.fbq;
      win.fbq.push = win.fbq;
      win.fbq.loaded = !0;
      win.fbq.version = '2.0';
      win.fbq.queue = [];
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    win.fbq('init', '1052676944287139');
    win.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen font-sans text-murdoku-dark bg-murdoku-cream overflow-x-hidden" data-id="element-0" data-mp="86mgf6usu4w7">
      
      {/* 1. Top Bar */}
      <div className="bg-murdoku-red text-white text-center py-3 px-4 shadow-md sticky top-0 z-50" data-id="element-1" data-mp="3glo2wfb6lqp3">
        <div className="font-heading font-bold text-lg md:text-xl tracking-widest flex items-center justify-center gap-2" data-id="element-2" data-mp="3bk702e94zu5v">
          <Zap size={20} className="animate-pulse text-yellow-300" data-id="element-3" data-mp="3lkhf84eqfbno" />
          <span data-id="element-4" data-mp="3lj6bu5p785x3">OFERTA DE LANZAMIENTO</span>
          <Countdown data-id="element-5" data-mp="2xudatutpz7vm" />
        </div>
        <div className="text-xs md:text-sm font-medium text-white/90 mt-1" data-id="element-6" data-mp="2xtlnt8e6uigy">
          Acceso digital inmediato después de tu compra
        </div>
      </div>

      {/* 2. Hero Section */}
      <section className="relative bg-murdoku-purple text-white pt-12 pb-20 overflow-hidden" data-id="element-7" data-mp="18cq57il9mcrv">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-8" data-mp="1d5jaq8ok5p50"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-murdoku-orange rounded-full blur-[100px] opacity-20 -translate-y-1/2 translate-x-1/3" data-id="element-9" data-mp="yfv6ayl2yad2"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-10" data-mp="13ey541agvkcl">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-11" data-mp="1r3zebqr4izix">
            
            {/* Hero Image */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6
          }} className="order-1 md:order-2 flex justify-center w-full mx-auto" data-id="element-12" data-mp="1w2kfpti43p8y">
              <img src={PACK_IMAGE_URL} alt="Pack Murdoku Completo" className="w-full max-w-[500px] object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" fetchPriority="high" data-id="element-13" data-mp="6muk30icqei4" />
            </motion.div>

            {/* Hero Text */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-2 md:order-1 space-y-6" data-id="element-14" data-mp="m6fc4ohwcrhd">
              <h1 className="font-heading text-5xl md:text-7xl font-black leading-[1.1] text-white drop-shadow-md uppercase text-center md:text-left" data-id="element-15" data-mp="ca2ifqmcqdmm">
                Dos libros.<br data-id="element-16" data-mp="14xydys06pzht" />
                <span className="text-murdoku-orange" data-id="element-17" data-mp="14xg6t0bzi0v4">160 misterios.</span><br data-id="element-18" data-mp="zxanc7fvbko4" />
                ¿Podrás resolverlos todos?
              </h1>
              
              <p className="text-lg md:text-xl text-white/90 font-medium leading-relaxed text-center md:text-left" data-id="element-19" data-mp="vwrss5mlc0i8">
                Descubrí el pack completo de Murdoku y poné a prueba tu lógica, atención y capacidad de deducción con 160 acertijos de crímenes y misterio.
              </p>

              <div className="bg-black/20 p-5 rounded-2xl border border-white/10" data-id="element-20" data-mp="1w4ovmhq01uvx">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-base font-medium" data-id="element-21" data-mp="1w2lwn86vcrf2">
                  {['2 libros completos', '80 acertijos en cada libro', 'Soluciones incluidas', 'Descarga inmediata'].map((bullet, i) => <li key={i} className="flex items-start gap-2" data-id="element-22" data-mp="29vjvj4iit1nd" data-mpi={i}>
                      <CheckCircle2 className="text-murdoku-orange shrink-0 mt-0.5" size={20} data-id="element-23" data-mp="29xwkw2qlyeag" />
                      <span data-id="element-24" data-mp="2ex0u2tdj8c0s">{bullet}</span>
                    </li>)}
                </ul>
              </div>

              <div className="pt-4 flex flex-col items-center md:items-start" data-id="element-25" data-mp="2jw3xk9dv6a6n">
                <div className="flex items-baseline gap-4 mb-3" data-id="element-26" data-mp="2nw4hg9jqfdl3">
                  <span className="text-5xl font-black text-murdoku-orange font-heading" data-id="element-27" data-mp="2suxtryna8yjo">
                    {currency === 'ARS' ? '$29.990' : 'USD 19,99'}
                  </span>
                  <span className="text-xl text-white/50 line-through decoration-murdoku-red decoration-2 font-bold" data-id="element-28" data-mp="ygetm2q7r74t">
                    {currency === 'ARS' ? '$39.980' : 'USD 39,99'}
                  </span>
                </div>

                <a href="#compra" onClick={e => {
                e.preventDefault();
                document.getElementById('compra')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="w-full md:w-auto bg-murdoku-orange hover:bg-orange-500 text-white font-black text-xl md:text-2xl py-5 px-12 rounded-xl shadow-[0_8px_0_#C2410C] hover:shadow-[0_4px_0_#C2410C] hover:translate-y-1 transition-all duration-200 flex items-center justify-center uppercase font-heading tracking-wider text-center" data-id="element-29" data-mp="1wtsoowfrqm5z">
                  QUIERO ACCEDER
                </a>

                <div className="flex items-center gap-2 mt-5 text-sm text-white/80 font-medium" data-id="element-30" data-mp="2pig265ua078z">
                  <ShieldCheck size={16} className="text-green-400" data-id="element-31" data-mp="33hpjj8u7x7ej" />
                  <span data-id="element-32" data-mp="2yicwoo3ck0by">Pago seguro · Producto digital · Acceso inmediato</span>
                </div>

                <div className="mt-4 inline-block bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg text-sm font-semibold text-center" data-id="element-33" data-mp="3i33gtfaoakvx">
                  ⚠️ No son muestras ni casos gratuitos: recibís ambos libros completos.
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Video Section */}
      

      {/* 4. Primer Libro */}
      <section className="py-20 bg-murdoku-cream relative overflow-hidden border-y border-gray-200" data-id="element-34" data-mp="2zc3t0hewzh48">
        <div className="max-w-6xl mx-auto px-6" data-id="element-35" data-mp="344wwl1yncjeq">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-36" data-mp="2pez6ggh5pjmw">
            <div className="order-1 md:order-1 flex justify-center relative" data-id="element-37" data-mp="2pgsf21bbydxj">
              <div className="absolute inset-0 bg-murdoku-purple/5 rounded-full blur-3xl" data-id="element-38" data-mp="2uetxck0xm97y"></div>
              <img src={COVER_MURDOKU} alt="Murdoku: 80 acertijos de lógica y asesinatos" className="w-full max-w-[450px] relative z-10 drop-shadow-2xl transform -rotate-2" data-id="element-39" data-mp="2gfc4ntkimbtb" />
            </div>
            <div className="order-2 md:order-2 space-y-6" data-id="element-40" data-mp="30uc882uli8lt">
              <div className="inline-block bg-murdoku-purple text-white font-bold px-4 py-1.5 rounded-full text-sm tracking-wider uppercase" data-id="element-41" data-mp="2vwaqbl8wofbx">
                LIBRO COMPLETO · 80 ACERTIJOS
              </div>
              <h3 className="font-heading text-4xl md:text-6xl font-black text-murdoku-dark uppercase leading-none" data-id="element-42" data-mp="2lyd2uzyvemkd">
                Murdoku: <br data-id="element-43" data-mp="2hwrlb1be5k3u" /><span className="text-murdoku-purple" data-id="element-44" data-mp="2hxkra02j05kx">80 acertijos de lógica y asesinatos</span>
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-45" data-mp="2d515wgxlk65k">
                Analizá la ubicación de cada sospechoso y utilizá la lógica para reconstruir la escena del crimen. Solo una combinación conduce a la verdad.
              </p>
              <ul className="space-y-3" data-id="element-46" data-mp="1y8iq0iejwy04">
                {['80 casos de dificultad progresiva.', 'Escenarios ilustrados y detallados.', 'Soluciones incluidas al final.'].map((item, i) => <li key={i} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm" data-id="element-47" data-mp="1u7qetjm4cj17" data-mpi={i}>
                    <Crosshair className="text-murdoku-purple shrink-0" size={20} data-id="element-48" data-mp="dqhonq3y68t1" />
                    <span className="font-semibold text-gray-800" data-id="element-49" data-mp="dskmuxf9788u">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Segundo Libro */}
      <section className="py-20 bg-murdoku-orange text-white relative overflow-hidden" data-id="element-50" data-mp="nmb6np0fqui7">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-51" data-mp="3m48850ypkqvv"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-52" data-mp="3r5xg27qmwofq">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-53" data-mp="3w47dyq3qu351">
            <div className="order-2 md:order-1 space-y-6" data-id="element-54" data-mp="4pt92qywo0e1">
              <div className="inline-block bg-white text-murdoku-orange font-bold px-4 py-1.5 rounded-full text-sm tracking-wider uppercase shadow-md" data-id="element-55" data-mp="4re3mgl7zjbw">
                LIBRO COMPLETO · 80 ACERTIJOS NUEVOS
              </div>
              <h3 className="font-heading text-4xl md:text-6xl font-black text-white uppercase leading-none" data-id="element-56" data-mp="38ctk2fpy1zhy">
                Murdoku: <br data-id="element-57" data-mp="2k685pv5a319z" /><span className="text-murdoku-dark" data-id="element-58" data-mp="2k5yclyoxse02">Viaje al Pasado</span>
              </h3>
              <p className="text-lg text-white/90 leading-relaxed" data-id="element-59" data-mp="2p5b795m1upd7">
                Viajá a escenarios históricos llenos de secretos. Aplicá la lógica en cantinas, palacios y aldeas para descubrir al culpable.
              </p>
              <ul className="space-y-3" data-id="element-60" data-mp="1whf53ldpo591">
                {['80 acertijos completamente nuevos.', 'Misterios en diferentes épocas.', 'No repite casos del primer libro.'].map((item, i) => <li key={i} className="flex items-center gap-3 bg-black/10 p-3 rounded-xl border border-white/10 backdrop-blur-sm" data-id="element-61" data-mp="26etg35gv81ty" data-mpi={i}>
                    <Map className="text-white shrink-0" size={20} data-id="element-62" data-mp="2b6lajr356rpd" />
                    <span className="font-semibold text-white" data-id="element-63" data-mp="1dqe4kswy01nd">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="order-1 md:order-2 flex justify-center relative" data-id="element-64" data-mp="1io7dvutjlbrv">
              <img src={COVER_VIAJE} alt="Murdoku: Viaje al Pasado" className="w-full max-w-[450px] relative z-10 drop-shadow-2xl transform rotate-2" data-id="element-65" data-mp="3w00jpbh5z22n" />
            </div>
          </div>
        </div>
      </section>

      {/* 6. Aclaración */}
      <section className="py-16 bg-murdoku-dark text-white relative" data-id="element-66" data-mp="3mwzgbijonp2o">
        <div className="max-w-5xl mx-auto px-6 text-center" data-id="element-67" data-mp="3hyo79a17pili">
          <h2 className="font-heading text-4xl md:text-5xl font-black mb-6 uppercase text-murdoku-red" data-id="element-68" data-mp="3d0mpcsfivpbm">
            Recibís los libros completos
          </h2>
          <p className="text-xl md:text-2xl font-medium text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed border-b border-white/10 pb-10" data-id="element-69" data-mp="388d4vrx6fhgq">
            Este pack <span className="text-white font-bold" data-id="element-70" data-mp="2z9roa2b9mcil">no contiene los casos gratuitos, demostraciones o muestras</span> que pueden encontrarse en internet. La compra incluye los dos libros completos, con 80 acertijos en cada uno y 160 desafíos en total.
          </p>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-71" data-mp="2pd51v93ajdeg">
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-72" data-mp="2fmeo0n2zg44s">
              <FileText className="mx-auto text-murdoku-orange mb-4" size={40} data-id="element-73" data-mp="2kn2ff9v4imzj" />
              <h4 className="font-heading font-bold text-2xl mb-2" data-id="element-74" data-mp="26mro65pwj6du">2 libros digitales completos</h4>
              <p className="text-gray-400 text-sm" data-id="element-75" data-mp="2aod8gc4rmmy2">No son capítulos sueltos ni versiones reducidas.</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-76" data-mp="2ydfojfjr47qg">
              <Brain className="mx-auto text-murdoku-purple mb-4" size={40} data-id="element-77" data-mp="33cr5opo15f8k" />
              <h4 className="font-heading font-bold text-2xl mb-2" data-id="element-78" data-mp="33f51j0qjavh6">160 acertijos en total</h4>
              <p className="text-gray-400 text-sm" data-id="element-79" data-mp="2pjjc4sge29o8">80 casos en cada libro listos para resolver.</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-80" data-mp="3hctkg311jsjl">
              <CheckCircle2 className="mx-auto text-green-400 mb-4" size={40} data-id="element-81" data-mp="3hap5mq1lt94j" />
              <h4 className="font-heading font-bold text-2xl mb-2" data-id="element-82" data-mp="3m53451bkbwdf">Soluciones incluidas</h4>
              <p className="text-gray-400 text-sm" data-id="element-83" data-mp="1an8b4iqidf0b">Podrás comprobar cada investigación al final.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Ideal Para */}
      <section className="py-20 bg-white border-t border-gray-200" data-id="element-84" data-mp="1kdb3qhtpwqy4">
        <div className="max-w-4xl mx-auto px-6" data-id="element-85" data-mp="1ffjejvkgjixm">
          <div className="bg-murdoku-purple text-white rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-86" data-mp="1ffrn5b9js230">
            <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-87" data-mp="rrrqjupk1hgi"></div>
            <div className="relative z-10" data-id="element-88" data-mp="ms5cjj1wjx6n">
              <h2 className="font-heading text-4xl md:text-5xl font-black mb-8 text-center uppercase text-murdoku-orange" data-id="element-89" data-mp="11nlba1dfgh9d">
                Este pack es para vos si…
              </h2>
              <ul className="grid md:grid-cols-2 gap-4 text-lg font-medium" data-id="element-90" data-mp="wr4jmx70rkrj">
                {['Te gustan los sudokus y juegos de lógica.', 'Disfrutás los misterios y casos criminales.', 'Querés entrenar la mente mientras te entretenés.', 'Te gusta resolver desafíos con amigos o en pareja.'].map((item, i) => <li key={i} className="flex items-start gap-3 bg-black/20 p-4 rounded-xl" data-id="element-91" data-mp="hp67mlxc4z3h" data-mpi={i}>
                    <CheckCircle2 className="text-murdoku-orange shrink-0 mt-0.5" size={24} data-id="element-92" data-mp="mo0p9kjkghm0" />
                    <span data-id="element-93" data-mp="qpm8roqlvu56">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 11. Testimonios */}
      <section className="py-20 bg-murdoku-cream overflow-hidden" data-id="element-94" data-mp="10kwasch081iy">
        <div className="max-w-6xl mx-auto px-6 text-center mb-12" data-id="element-95" data-mp="15k8tqm4r8og9">
          <h2 className="font-heading text-4xl md:text-5xl font-black text-murdoku-dark mb-4 uppercase" data-id="element-96" data-mp="15ifmb4mbi077">
            Lo que dicen quienes ya lo compraron
          </h2>
        </div>
        <TestimonialCarousel data-id="element-97" data-mp="1ecaqoafm7mls" />
      </section>

      {/* 12. CTA Final */}
      <section id="compra" className="py-24 bg-gradient-to-b from-murdoku-purple to-murdoku-dark text-white relative overflow-hidden" data-id="element-98" data-mp="1jdi0ef1bivv3">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-99" data-mp="1o95mumof1aww"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-100" data-mp="1ygru51xzy5zl">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-101" data-mp="1tfudwuwjrm0q">
            
            <div className="w-[90%] md:w-[50%] mx-auto order-1 flex justify-center" data-id="element-102" data-mp="1oghg5f450ujd">
              <img src={PACK_IMAGE_URL} alt="Pack Murdoku Completo" className="w-full max-w-[500px] object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.8)]" loading="lazy" data-id="element-103" data-mp="1kffhaze4ukhx" />
            </div>

            <div className="w-full md:w-[50%] text-center md:text-left order-2" data-id="element-104" data-mp="2c6ljrmhbsuad">
              <h2 className="font-heading text-5xl md:text-6xl font-black mb-6 text-white uppercase leading-[1.1]" data-id="element-105" data-mp="2c8ohytsmttq6">
                Dos aventuras completas.<br data-id="element-106" data-mp="23etdzp38y9c4" />
                <span className="text-murdoku-orange" data-id="element-107" data-mp="23d8fjodta68z">160 crímenes por resolver.</span>
              </h2>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl w-full" data-id="element-108" data-mp="2zwzddhfk7172">
                <div className="flex items-baseline justify-center md:justify-start gap-4 mb-2" data-id="element-109" data-mp="2v3wjt9as7rqv">
                  <span className="text-6xl font-black text-murdoku-orange font-heading" data-id="element-110" data-mp="t8fhg89ilm8o">
                    {currency === 'ARS' ? '$29.990' : 'USD 19,99'}
                  </span>
                  <span className="text-2xl text-white/50 line-through decoration-murdoku-red decoration-2 font-bold" data-id="element-111" data-mp="jh2thrqu7g7x">
                    {currency === 'ARS' ? '$39.980' : 'USD 39,99'}
                  </span>
                </div>
                <p className="text-sm text-white/80 font-medium mb-8 text-center md:text-left" data-id="element-112" data-mp="ajiny6lxyxri">
                  Precio especial del pack
                </p>

                <a href={CHECKOUT_URL} className="block w-full bg-murdoku-orange hover:bg-orange-500 text-white font-black text-xl md:text-2xl py-5 px-8 rounded-xl shadow-[0_8px_0_#C2410C] hover:shadow-[0_4px_0_#C2410C] hover:translate-y-1 transition-all duration-200 uppercase text-center font-heading tracking-wider" data-id="element-113" data-mp="kbbrmqy195z">
                  QUIERO EL PACK
                </a>

                <div className="flex flex-col items-center md:items-start gap-3 mt-6 text-sm text-white/80 font-medium" data-id="element-114" data-mp="3ke50rl2g3rd4">
                  <div className="flex items-center gap-2" data-id="element-115" data-mp="3fg3b2haiktso">
                    <ShieldCheck size={18} className="text-green-400" data-id="element-116" data-mp="3u47szj1qt1ae" />
                    <span data-id="element-117" data-mp="3u5iybnau6h3m">Producto digital · Acceso inmediato · Pago seguro</span>
                  </div>
                  <div className="text-xs text-white/50" data-id="element-118" data-mp="3pbeomwa6ruyu">
                    ⚠️ No se envía ningún producto físico. Los archivos se entregan digitalmente.
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 13. FAQ */}
      <section className="py-20 bg-white" data-id="element-119" data-mp="gvli9a9kjzvi">
        <div className="max-w-4xl mx-auto px-6" data-id="element-120" data-mp="bybm7brocpyp">
          <h2 className="font-heading text-4xl md:text-5xl font-black text-center text-murdoku-dark mb-12 uppercase" data-id="element-121" data-mp="bwidzs1exxol">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-122" data-mp="2dqzptm88dff0" />
        </div>
      </section>

      {/* 14. Footer */}
      <footer className="bg-murdoku-dark text-white/70 py-16 border-t border-white/10" data-id="element-123" data-mp="1rxbxhhtnpkrt">
        <div className="max-w-6xl mx-auto px-6" data-id="element-124" data-mp="1wvltfun7gpeh">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-10 mb-12" data-id="element-125" data-mp="1wu2f49l40ofc">
            <div className="text-center md:text-left max-w-md" data-id="element-126" data-mp="21oos2h6uap1j">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-127" data-mp="26kndbvwvfqxq">
                <div className="bg-murdoku-purple p-2 rounded-lg" data-id="element-128" data-mp="18yzobw2qwaw8">
                  <Brain className="text-murdoku-orange" size={24} data-id="element-129" data-mp="1dwrg8q3e3x0f" />
                </div>
                <h3 className="font-heading font-bold text-3xl text-white tracking-wide uppercase" data-id="element-130" data-mp="1iw43vd235842">
                  Atlas Digital Hub
                </h3>
              </div>
              <p className="text-lg text-white/90 font-medium mb-2" data-id="element-131" data-mp="1nt48djjae2t2">
                Productos digitales para aprender, crear y entretenerte
              </p>
              <p className="text-sm text-white/50 mb-4" data-id="element-132" data-mp="2z9b0xy53eseg">
                Tienda digital de recursos, libros y productos descargables
              </p>
            </div>

            <div className="flex flex-col items-center md:items-end gap-4 text-sm" data-id="element-133" data-mp="io6003n142c8">
              <a href="#" className="hover:text-white transition-colors" data-id="element-134" data-mp="iqa3wlkxh1ci">Términos y Condiciones</a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-135" data-mp="duurly2kjdi7">Políticas de Privacidad</a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-136" data-mp="8w9yeilqhu39">Contacto</a>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 text-center text-xs text-white/40 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-137" data-mp="3w32a96rw3xly">
            <p data-id="element-138" data-mp="3r3zi2mx0dlve">© {new Date().getFullYear()} Atlas Digital Hub. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      <PurchasePopup data-id="element-139" data-mp="1pc49wh9ynzhx" />
      
      <noscript data-id="element-140" data-mp="1pbbc45szckbx">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1052676944287139&ev=PageView&noscript=1" alt="" data-id="element-141" data-mp="1pbuhkn1g1g92" />
      </noscript>
    </div>;
}